import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Library,
  Search,
  Plus,
  Filter,
  Grid3x3,
  List,
  Star,
  Copy,
  Download,
  Upload,
  Edit2,
  Trash2,
  Clock,
  Tag,
  TrendingUp,
  Folder,
  ChevronRight
} from 'lucide-react';

import CreatePromptModal from '../components/modals/CreatePromptModal';
import ImportModal from '../components/modals/ImportModal';
import EditPromptModal from '../components/modals/EditPromptModal';
import SkeletonLoader from '../components/common/SkeletonLoader';
import { useToast } from '../contexts/ToastContext';
import { useDebounce } from '../hooks/useDebounce';

const PromptLibrary = () => {
  const toast = useToast();
  const [prompts, setPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  
  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [filters, setFilters] = useState({
    is_favorite: false,
    is_system: null  // null = show all, true = system only, false = user only
  });
  
  // Modals
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [editingPrompt, setEditingPrompt] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    favorites: 0,
    system: 0,
    custom: 0
  });

  useEffect(() => {
    fetchCategories();
    fetchTags();
    fetchPrompts();
  }, [selectedCategory, selectedTags, sortBy, filters, debouncedSearchQuery]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (debouncedSearchQuery) params.append('q', debouncedSearchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));
      if (filters.is_favorite) params.append('is_favorite', 'true');
      if (filters.is_system !== null) params.append('is_system', filters.is_system.toString());
      params.append('sort_by', sortBy);
      params.append('limit', '100');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts-library/search?${params}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      
      if (data.success) {
        setPrompts(data.prompts || []);
        calculateStats(data.prompts || []);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts-library/categories`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts-library/tags`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setTags(data.tags || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const calculateStats = (promptsList) => {
    setStats({
      total: promptsList.length,
      favorites: promptsList.filter(p => p.is_favorite).length,
      system: promptsList.filter(p => p.is_system_template).length,
      custom: promptsList.filter(p => !p.is_system_template).length
    });
  };

  const handleDuplicate = async (promptId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts-library/${promptId}/duplicate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({})
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        toast.error(`Erreur lors de la duplication: ${errorData.detail || 'Erreur inconnue'}`);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        fetchPrompts();
        toast.success('Prompt dupliqué avec succès !');
      } else {
        toast.error('Erreur lors de la duplication');
      }
    } catch (error) {
      console.error('Error duplicating prompt:', error);
      toast.error('Erreur réseau lors de la duplication');
    }
  };

  const handleExport = async () => {
    if (selectedPrompts.length === 0) {
      toast.warning('Sélectionnez au moins un prompt à exporter');
      return;
    }
    
    // Filter out undefined IDs
    const validIds = selectedPrompts.filter(id => id !== undefined && id !== null);
    
    if (validIds.length === 0) {
      toast.error('Aucun prompt valide sélectionné');
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts-library/export`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ prompt_ids: validIds })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        toast.error(`Erreur lors de l'export: ${errorData.detail || 'Erreur inconnue'}`);
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nexora_prompts_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setSelectedPrompts([]);
      toast.success(`${validIds.length} prompts exportés avec succès !`);
    } catch (error) {
      console.error('Error exporting prompts:', error);
      toast.error('Erreur réseau lors de l\'export');
    }
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setShowEditModal(true);
  };

  const handleToggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const getCategoryIcon = (categoryName) => {
    const icons = {
      'Marketing': '📣',
      'E-commerce': '🛍️',
      'Social Media': '📱',
      'Email': '✉️',
      'Blog': '📝',
      'Ads': '🎯',
      'SEO': '🔍',
      'Custom': '⚙️'
    };
    return icons[categoryName] || '📁';
  };

  if (loading && prompts.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex">
        <aside className="w-64 border-r border-white/10 p-6">
          <SkeletonLoader variant="text" count={1} />
        </aside>
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <SkeletonLoader variant="text" count={1} className="mb-8" />
            <div className="grid grid-cols-4 gap-4 mb-8">
              <SkeletonLoader variant="stat" count={4} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <SkeletonLoader variant="card" count={6} />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex">
      {/* Sidebar - Categories */}
      <aside className="w-64 border-r border-white/10 p-6 overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Folder size={20} className="text-cyan-400" />
            Catégories
          </h2>
          
          {/* All Prompts */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedCategory(null)}
            className={`w-full text-left px-4 py-3 rounded-lg mb-2 transition-all ${
              selectedCategory === null
                ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30'
                : 'text-slate-300 hover:bg-white/5'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Library size={16} />
                Tous les prompts
              </span>
              <span className="text-xs bg-slate-700 px-2 py-1 rounded-full">{stats.total}</span>
            </div>
          </motion.button>

          {/* Categories */}
          {categories.map((category, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedCategory(category.name)}
              className={`w-full text-left px-4 py-3 rounded-lg mb-2 transition-all ${
                selectedCategory === category.name
                  ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'text-slate-300 hover:bg-white/5'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span>{getCategoryIcon(category.name)}</span>
                  {category.name}
                </span>
                <ChevronRight size={14} />
              </div>
            </motion.button>
          ))}
        </div>

        {/* Quick Filters */}
        <div className="pt-4 border-t border-white/10">
          <h3 className="text-sm font-medium text-slate-400 mb-3">Filtres Rapides</h3>
          
          <label className="flex items-center gap-2 mb-3 cursor-pointer text-slate-300 hover:text-white transition-colors">
            <input
              type="checkbox"
              checked={filters.is_favorite}
              onChange={(e) => setFilters({ ...filters, is_favorite: e.target.checked })}
              className="w-4 h-4 rounded border-2 border-cyan-500 bg-slate-800 checked:bg-cyan-500 cursor-pointer"
            />
            <Star size={14} className="text-yellow-400" />
            <span className="text-sm">Favoris uniquement</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer text-slate-300 hover:text-white transition-colors">
            <input
              type="checkbox"
              checked={filters.is_system === true}
              onChange={(e) => setFilters({ ...filters, is_system: e.target.checked ? true : null })}
              className="w-4 h-4 rounded border-2 border-cyan-500 bg-slate-800 checked:bg-cyan-500 cursor-pointer"
            />
            <span className="text-sm">Templates système</span>
          </label>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Library className="inline mr-3 text-cyan-400" size={36} />
                Bibliothèque de Prompts
              </h1>
              <p className="text-slate-400 text-lg">
                {selectedCategory 
                  ? `Catégorie : ${selectedCategory}` 
                  : 'Explorez et gérez vos templates AI'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowImportModal(true)}
                className="px-4 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all flex items-center gap-2"
              >
                <Upload size={20} />
                Importer
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleExport}
                disabled={selectedPrompts.length === 0}
                className="px-4 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={20} />
                Exporter ({selectedPrompts.length})
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="px-4 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all"
              >
                {viewMode === 'grid' ? <List size={20} /> : <Grid3x3 size={20} />}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center gap-2"
              >
                <Plus size={20} />
                Nouveau Prompt
              </motion.button>
            </div>
          </div>

          {/* Search & Sort */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher dans vos prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-white/10 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500 transition-all"
              />
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500 transition-all"
            >
              <option value="recent">Plus récents</option>
              <option value="popular">Plus populaires</option>
              <option value="name">Par nom</option>
              <option value="usage">Plus utilisés</option>
            </select>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowFilters(!showFilters)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                showFilters || selectedTags.length > 0
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                  : 'bg-slate-800 text-slate-300 border border-white/10'
              }`}
            >
              <Filter size={20} />
              Tags {selectedTags.length > 0 && `(${selectedTags.length})`}
            </motion.button>
          </div>

          {/* Tags Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6"
              >
                <div className="flex flex-wrap gap-2">
                  {tags.slice(0, 20).map((tagData, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleToggleTag(tagData.tag)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                        selectedTags.includes(tagData.tag)
                          ? 'bg-cyan-500 text-white'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <Tag size={14} />
                      {tagData.tag}
                      <span className="text-xs opacity-70">({tagData.usage_count})</span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stats Bar */}
          <div className="grid grid-cols-4 gap-4">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
                <Library className="text-cyan-400" size={24} />
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Favoris</p>
                  <p className="text-2xl font-bold text-white">{stats.favorites}</p>
                </div>
                <Star className="text-yellow-400" size={24} />
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Système</p>
                  <p className="text-2xl font-bold text-white">{stats.system}</p>
                </div>
                <TrendingUp className="text-blue-400" size={24} />
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Personnalisés</p>
                  <p className="text-2xl font-bold text-white">{stats.custom}</p>
                </div>
                <Edit2 className="text-purple-400" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Prompts Grid/List */}
        {prompts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-12 text-center"
          >
            <Library className="mx-auto text-slate-600 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-white mb-2">Aucun prompt trouvé</h3>
            <p className="text-slate-400 mb-6">
              {searchQuery || selectedTags.length > 0 || selectedCategory
                ? 'Essayez de modifier vos filtres de recherche'
                : 'Commencez par créer votre premier prompt ou importer des templates'}
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all inline-flex items-center gap-2"
            >
              <Plus size={20} />
              Créer mon premier prompt
            </motion.button>
          </motion.div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prompts.map((prompt, index) => (
              <PromptCard
                key={prompt.id || index}
                prompt={prompt}
                index={index}
                onDuplicate={handleDuplicate}
                isSelected={selectedPrompts.includes(prompt.id)}
                onToggleSelect={(id) => {
                  setSelectedPrompts(prev =>
                    prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
                  );
                }}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {prompts.map((prompt, index) => (
              <PromptListItem
                key={prompt.id || index}
                prompt={prompt}
                index={index}
                onDuplicate={handleDuplicate}
                isSelected={selectedPrompts.includes(prompt.id)}
                onToggleSelect={(id) => {
                  setSelectedPrompts(prev =>
                    prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
                  );
                }}
              />
            ))}
          </div>
        )}
      </main>

      {/* Modals */}
      <CreatePromptModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={() => fetchPrompts()}
      />
      
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => fetchPrompts()}
      />

      <EditPromptModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPrompt(null);
        }}
        prompt={editingPrompt}
        onUpdate={() => fetchPrompts()}
      />
    </div>
  );
};

// Prompt Card Component
const PromptCard = ({ prompt, index, onDuplicate, isSelected, onToggleSelect }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`backdrop-blur-xl bg-white/5 border rounded-2xl p-6 hover:bg-white/10 transition-all duration-200 cursor-pointer ${
        isSelected ? 'border-cyan-500' : 'border-white/10'
      }`}
      onClick={() => onToggleSelect(prompt.id)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-white">{prompt.name}</h3>
            {prompt.is_system_template && (
              <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-md border border-blue-500/30">
                Système
              </span>
            )}
          </div>
          <p className="text-sm text-slate-400 mb-3">{prompt.description}</p>
          
          <span className="text-xs px-2 py-1 bg-indigo-500/20 text-indigo-400 rounded-md border border-indigo-500/30">
            {prompt.category}
          </span>
        </div>
      </div>

      {/* Template Preview */}
      <div className="bg-slate-900/50 rounded-xl p-3 mb-4">
        <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono line-clamp-3">
          {prompt.template}
        </pre>
      </div>

      {/* Tags */}
      {prompt.tags && prompt.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {prompt.tags.slice(0, 3).map((tag, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-cyan-500/10 text-cyan-400 rounded-md">
              #{tag}
            </span>
          ))}
          {prompt.tags.length > 3 && (
            <span className="text-xs text-slate-500">+{prompt.tags.length - 3}</span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Clock size={12} />
            v{prompt.version || 1}
          </span>
          {prompt.usage_count > 0 && (
            <span className="flex items-center gap-1">
              <TrendingUp size={12} />
              {prompt.usage_count}× utilisé
            </span>
          )}
        </div>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(prompt.id);
          }}
          className="p-2 hover:bg-cyan-500/20 rounded-lg transition-all"
        >
          <Copy size={14} className="text-cyan-400" />
        </motion.button>
      </div>
    </motion.div>
  );
};

// Prompt List Item Component
const PromptListItem = ({ prompt, index, onDuplicate, isSelected, onToggleSelect }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`backdrop-blur-xl bg-white/5 border rounded-xl p-4 hover:bg-white/10 transition-all duration-200 flex items-center gap-4 cursor-pointer ${
        isSelected ? 'border-cyan-500' : 'border-white/10'
      }`}
      onClick={() => onToggleSelect(prompt.id)}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => {}}
        className="w-4 h-4 rounded border-2 border-cyan-500 bg-slate-800 checked:bg-cyan-500 cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-semibold text-white">{prompt.name}</h3>
          {prompt.is_system_template && (
            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-md">Système</span>
          )}
          <span className="text-xs px-2 py-0.5 bg-indigo-500/20 text-indigo-400 rounded-md">{prompt.category}</span>
        </div>
        <p className="text-xs text-slate-400 truncate">{prompt.description}</p>
      </div>

      {prompt.tags && prompt.tags.length > 0 && (
        <div className="flex gap-1">
          {prompt.tags.slice(0, 2).map((tag, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-cyan-500/10 text-cyan-400 rounded-md">
              #{tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">v{prompt.version || 1}</span>
        {prompt.usage_count > 0 && (
          <span className="text-xs text-slate-500">{prompt.usage_count}×</span>
        )}
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(prompt.id);
          }}
          className="p-2 hover:bg-cyan-500/20 rounded-lg transition-all"
        >
          <Copy size={14} className="text-cyan-400" />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default PromptLibrary;
